import { useState } from 'react';
import { CheckCircle, AlertCircle, Settings, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';

interface Integration {
  id: string;
  name: string;
  description: string;
  status: 'connected' | 'disconnected' | 'error';
  lastSync: string;
  version: string;
  settings: Record<string, any>;
}

const mockIntegrations: Integration[] = [
  {
    id: 'grafana',
    name: 'Grafana',
    description: 'Real-time metrics and monitoring dashboards',
    status: 'connected',
    lastSync: '2024-01-24T10:30:00Z',
    version: '10.2.3',
    settings: {
      url: 'https://grafana.company.com',
      apiKey: 'glsa_***',
      syncInterval: 60
    }
  },
  {
    id: 'datadog',
    name: 'Datadog',
    description: 'Application performance monitoring and logging',
    status: 'connected',
    lastSync: '2024-01-24T10:25:00Z',
    version: '7.51.0',
    settings: {
      apiKey: 'dd_***',
      appKey: 'app_***',
      site: 'datadoghq.com'
    }
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Team notifications and alerts',
    status: 'connected',
    lastSync: '2024-01-24T10:15:00Z',
    version: '2.1.0',
    settings: {
      webhook: 'https://hooks.slack.com/***',
      channel: '#alerts',
      enabled: true
    }
  },
  {
    id: 'github',
    name: 'GitHub',
    description: 'Version control and deployment tracking',
    status: 'error',
    lastSync: '2024-01-23T16:45:00Z',
    version: '1.5.2',
    settings: {
      token: 'ghp_***',
      repository: 'company/ai-bots',
      branch: 'main'
    }
  },
  {
    id: 'pagerduty',
    name: 'PagerDuty',
    description: 'Incident management and alerting',
    status: 'disconnected',
    lastSync: 'Never',
    version: '3.2.1',
    settings: {
      integrationKey: '',
      enabled: false
    }
  }
];

export function IntegrationsConfig() {
  const [integrations, setIntegrations] = useState(mockIntegrations);

  const formatDate = (dateString: string) => {
    if (dateString === 'Never') return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'disconnected':
        return <AlertCircle className="w-4 h-4 text-gray-400" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      connected: 'default',
      disconnected: 'secondary',
      error: 'destructive'
    };
    return variants[status as keyof typeof variants] || 'outline';
  };

  const toggleIntegration = (id: string) => {
    setIntegrations(prev => prev.map(integration => {
      if (integration.id === id) {
        const newStatus = integration.status === 'connected' ? 'disconnected' : 'connected';
        return {
          ...integration,
          status: newStatus,
          lastSync: newStatus === 'connected' ? new Date().toISOString() : integration.lastSync
        };
      }
      return integration;
    }));
  };

  const configureIntegration = (id: string) => {
    console.log('Configuring integration:', id);
  };

  const testIntegration = (id: string) => {
    console.log('Testing integration:', id);
  };

  return (
    <div className="space-y-6">
      {/* Active Integrations */}
      <Card>
        <CardHeader>
          <CardTitle>Available Integrations</CardTitle>
          <div className="text-sm text-muted-foreground">
            Connect external services to enhance monitoring and automation
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            {integrations.map((integration) => (
              <Card key={integration.id} className="p-4">
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <Settings className="w-4 h-4" />
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-sm">{integration.name}</h3>
                          <Badge variant="outline" className="text-xs">
                            v{integration.version}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {integration.description}
                        </p>
                        <div className="text-xs text-muted-foreground">
                          Last sync: {formatDate(integration.lastSync)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(integration.status)}
                        <Badge variant={getStatusBadge(integration.status) as any} className="text-xs">
                          {integration.status}
                        </Badge>
                      </div>
                      <Switch
                        checked={integration.status === 'connected'}
                        onCheckedChange={() => toggleIntegration(integration.id)}
                      />
                    </div>
                  </div>

                  {integration.status === 'connected' && (
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => configureIntegration(integration.id)}
                      >
                        <Settings className="w-3 h-3 mr-1" />
                        Configure
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => testIntegration(integration.id)}
                      >
                        Test Connection
                      </Button>
                      {integration.id === 'grafana' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(integration.settings.url, '_blank')}
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Open Dashboard
                        </Button>
                      )}
                    </div>
                  )}

                  {integration.status === 'error' && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-red-500" />
                        <span className="text-sm font-medium text-red-800">Connection Error</span>
                      </div>
                      <p className="text-sm text-red-700 mt-1">
                        {integration.id === 'github' 
                          ? 'Invalid GitHub token or repository access denied'
                          : 'Failed to establish connection'
                        }
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => configureIntegration(integration.id)}
                      >
                        Fix Configuration
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Integration Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Integration Health</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-green-600">
                {integrations.filter(i => i.status === 'connected').length}
              </div>
              <div className="text-sm text-muted-foreground">Connected</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">
                {integrations.filter(i => i.status === 'error').length}
              </div>
              <div className="text-sm text-muted-foreground">Errors</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-600">
                {integrations.filter(i => i.status === 'disconnected').length}
              </div>
              <div className="text-sm text-muted-foreground">Disconnected</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sync Settings */}
      <Card>
        <CardHeader>
          <CardTitle>Sync Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-sm">Auto-sync Enabled</div>
              <div className="text-xs text-muted-foreground">
                Automatically sync data from connected integrations
              </div>
            </div>
            <Switch defaultChecked />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-sm">Sync Interval</div>
              <div className="text-xs text-muted-foreground">
                How often to fetch data from integrations
              </div>
            </div>
            <span className="text-sm font-mono">5 minutes</span>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-sm">Error Notifications</div>
              <div className="text-xs text-muted-foreground">
                Send alerts when integrations fail
              </div>
            </div>
            <Switch defaultChecked />
          </div>
          
          <Button className="w-full">
            Sync All Integrations Now
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}